import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_PORTAL_VISIBILITIES } from '@lims/shared-types';
import { clientsApi, portalAccountsApi } from '../../api/endpoints.js';
import type { ClientContact } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  Dialog,
  ErrorState,
  Form,
  FormSection,
  LoadingState,
  PageHeader,
  Panel,
  SelectField,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,

} from '../../components/ui/index.js';
import { optionalText } from './bilingual.js';
import { PortalAccountPanel } from './PortalAccountPanel.js';

/**
 * The people at one client, and what each of them would see in the client
 * portal.
 *
 * THE PORTAL SWITCH IS ELIGIBILITY, NOT ACCESS (Phase 11). Enabling a contact
 * records a DECISION the laboratory has made about a person; it creates no
 * account and sends nobody an invitation. The account itself is a separate
 * record, created and managed in each contact's `PortalAccountPanel`, and
 * the form says so in place (CLAUDE.md section 8).
 *
 * TWO PERMISSIONS. Editing a contact needs `clients.update`. Changing portal
 * eligibility needs `portal_accounts.manage` as well, and the switches are
 * disabled without it — which is a courtesy, not the control: the server
 * refuses the same change regardless.
 *
 * NOBODY IS DELETED. A contact is deactivated. A report was sent to this
 * person, and that stays true after they leave the company.
 */

interface ContactDraft {
  name: string;
  position: string;
  email: string;
  phone: string;
  mobile: string;
  isPrimary: boolean;
  portalEnabled: boolean;
  portalVisibility: string;
  notes: string;
}

function emptyDraft(): ContactDraft {
  return {
    name: '',
    position: '',
    email: '',
    phone: '',
    mobile: '',
    isPrimary: false,
    portalEnabled: false,
    portalVisibility: 'none',
    notes: '',
  };
}

function draftFrom(contact: ClientContact): ContactDraft {
  return {
    name: contact.name,
    position: contact.position ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    mobile: contact.mobile ?? '',
    isPrimary: contact.isPrimary,
    portalEnabled: contact.portalEnabled,
    portalVisibility: contact.portalVisibility,
    notes: contact.notes ?? '',
  };
}

export function ClientContactsPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();

  const canEdit = can(PERMISSIONS.CLIENTS_UPDATE);
  const canManagePortal = can(PERMISSIONS.PORTAL_ACCOUNTS_MANAGE);

  const [showInactive, setShowInactive] = useState(true);
  const [editing, setEditing] = useState<ClientContact | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft());
  const [deactivating, setDeactivating] = useState<ClientContact | null>(null);

  const client = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.get(id),
    enabled: Boolean(id),
  });

  const contacts = useQuery({
    queryKey: ['client-contacts', id, showInactive],
    queryFn: () => clientsApi.listContacts(id, showInactive),
    enabled: Boolean(id),
  });

  const portalAccounts = useQuery({
    queryKey: ['portal-accounts', id],
    queryFn: () => portalAccountsApi.list(id),
    enabled: Boolean(id) && canManagePortal,
  });
  const accountFor = (contactId: string) =>
    portalAccounts.data?.find((account) => account.contact.id === contactId);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['client-contacts', id] });

  const payload = () => ({
    name: draft.name.trim(),
    position: optionalText(draft.position),
    email: optionalText(draft.email),
    phone: optionalText(draft.phone),
    mobile: optionalText(draft.mobile),
    isPrimary: draft.isPrimary,
    // Sent only when this caller may actually change them. Sending an
    // unchanged value would be harmless; sending a value they may not set
    // would earn a 403 for a change they never asked for.
    ...(canManagePortal
      ? {
          portalEnabled: draft.portalEnabled,
          portalVisibility: draft.portalEnabled
            ? draft.portalVisibility === 'none'
              ? 'reports'
              : draft.portalVisibility
            : 'none',
        }
      : {}),
    notes: optionalText(draft.notes),
  });

  const save = useMutation({
    mutationFn: () =>
      editing
        ? clientsApi.updateContact(id, editing.id, payload())
        : clientsApi.createContact(id, payload()),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setIsCreating(false);
      setDraft(emptyDraft());
      show({ tone: 'success', message: t('contacts.saved') });
    },
  });

  const setActive = useMutation({
    mutationFn: (input: { contact: ClientContact; isActive: boolean }) =>
      clientsApi.updateContact(id, input.contact.id, {
        isActive: input.isActive,
      }),
    onSuccess: async () => {
      await refresh();
      setDeactivating(null);
      show({ tone: 'success', message: t('contacts.saved') });
    },
  });

  const openCreate = () => {
    setDraft(emptyDraft());
    setEditing(null);
    setIsCreating(true);
    save.reset();
  };

  const openEdit = (contact: ClientContact) => {
    setDraft(draftFrom(contact));
    setEditing(contact);
    setIsCreating(false);
    save.reset();
  };

  const closeDialog = () => {
    setEditing(null);
    setIsCreating(false);
    save.reset();
  };

  if (contacts.isLoading) return <LoadingState label={t('common.loading')} />;
  if (contacts.error) {
    return (
      <ErrorState
        error={contacts.error}
        onRetry={() => void contacts.refetch()}
      />
    );
  }

  const portalError =
    save.error instanceof ApiError &&
    save.error.details?.some(
      (detail) => detail.code === 'email_required_for_portal',
    );
  const permissionError =
    save.error instanceof ApiError && save.error.code === 'PERMISSION_DENIED';

  const rows = contacts.data ?? [];

  return (
    <>
      <PageHeader
        title={t('contacts.title')}
        subtitle={client.data?.name ?? t('contacts.subtitle')}
        breadcrumbs={[
          { label: t('nav.clients'), to: '/clients' },
          {
            label: client.data?.clientNumber ?? t('clients.file.title'),
            to: `/clients/${id}`,
          },
          { label: t('contacts.title') },
        ]}
        actions={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => navigate(`/clients/${id}`)}
            >
              {t('common.back')}
            </ActionButton>
            {canEdit ? (
              <ActionButton priority="primary" onPress={openCreate}>
                {t('contacts.add')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      {/*
        One panel, one row per person (D278) — not a card each. The row
        carries who they are, how to reach them, what the portal may show them
        and, for a portal administrator, their account.
      */}
      <Panel
        title={t('contacts.title')}
        description={t('contacts.subtitle')}
        actions={
          <SwitchField
            label={t('contacts.showInactive')}
            value={showInactive}
            onChange={setShowInactive}
          />
        }
      >
        {rows.length === 0 ? (
          <p className="text-body text-default-600">{t('contacts.noneYet')}</p>
        ) : (
          <ul className="-my-3 flex flex-col divide-y divide-default-200">
            {rows.map((contact) => (
              <li key={contact.id} className="py-3">
                <div className="grid gap-stack md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body font-semibold text-default-900" dir="auto">
                        {contact.name ?? t('common.notRecorded')}
                      </p>
                      {contact.isPrimary ? (
                        <StatusBadge
                          size="sm"
                          tone="info"
                          label={t('contacts.primaryBadge')}
                        />
                      ) : null}
                      {contact.isActive ? null : (
                        <StatusBadge
                          size="sm"
                          tone="neutral"
                          label={t('common.inactive')}
                        />
                      )}
                    </div>
                    <p className="text-caption text-default-600" dir="auto">
                      {contact.position ?? t('common.notRecorded')}
                    </p>
                  </div>

                  <div className="flex min-w-0 flex-col gap-1 text-caption text-default-700">
                    <span dir="ltr" className="break-all text-start">
                      {contact.email ?? t('common.notRecorded')}
                    </span>
                    <span dir="ltr" className="text-start">
                      {contact.phone ?? contact.mobile ?? t('common.notRecorded')}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-default-600">
                        {t('contacts.portalVisibilityLabel')}
                      </span>
                      <StatusBadge
                        size="sm"
                        tone={contact.portalEnabled ? 'info' : 'neutral'}
                        label={t(
                          `contacts.portalVisibility.${contact.portalVisibility}`,
                        )}
                      />
                    </span>
                  </div>

                  {canEdit ? (
                    <div className="md:justify-self-end">
                      <ActionBar>
                        {contact.isActive ? (
                          <ActionButton
                            priority="tertiary"
                            size="sm"
                            onPress={() => setDeactivating(contact)}
                          >
                            {t('contacts.deactivate')}
                          </ActionButton>
                        ) : (
                          <ActionButton
                            priority="tertiary"
                            size="sm"
                            onPress={() =>
                              setActive.mutate({ contact, isActive: true })
                            }
                          >
                            {t('contacts.reactivate')}
                          </ActionButton>
                        )}
                        <ActionButton
                          priority="secondary"
                          size="sm"
                          onPress={() => openEdit(contact)}
                        >
                          {t('common.edit')}
                        </ActionButton>
                      </ActionBar>
                    </div>
                  ) : null}
                </div>

                {canManagePortal ? (
                  <div className="mt-3">
                    <PortalAccountPanel
                      clientId={id}
                      contact={contact}
                      account={accountFor(contact.id)}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Dialog
        isOpen={isCreating || editing !== null}
        onClose={closeDialog}
        title={editing ? t('contacts.editTitle') : t('contacts.addTitle')}
        size="lg"
      >
        {portalError ? (
          <div className="mb-stack">
            <Alert tone="warning" title={t('contacts.portalNeedsEmail')} />
          </div>
        ) : null}
        {permissionError ? (
          <div className="mb-stack">
            <Alert
              tone="danger"
              title={t('contacts.portalPermissionRequired')}
            />
          </div>
        ) : null}

        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={closeDialog}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={save.isPending}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('common.details')}>
            <TextField
              label={t('contacts.name')}
              value={draft.name}
              onChange={(value) => setDraft((c) => ({ ...c, name: value }))}
              textDirection="auto"
              isRequired
            />
            <TextField
              label={t('contacts.position')}
              value={draft.position}
              onChange={(value) => setDraft((c) => ({ ...c, position: value }))}
              textDirection="auto"
            />
            <SwitchField
              label={t('contacts.isPrimary')}
              description={t('contacts.isPrimaryHint')}
              value={draft.isPrimary}
              onChange={(value) =>
                setDraft((c) => ({ ...c, isPrimary: value }))
              }
            />
          </FormSection>

          <FormSection title={t('clients.contactDetails')} columns={2}>
            <TextField
              label={t('contacts.email')}
              type="email"
              value={draft.email}
              onChange={(value) => setDraft((c) => ({ ...c, email: value }))}
            />
            <TextField
              label={t('contacts.phone')}
              type="tel"
              value={draft.phone}
              onChange={(value) => setDraft((c) => ({ ...c, phone: value }))}
            />
            <TextField
              label={t('contacts.mobile')}
              type="tel"
              value={draft.mobile}
              onChange={(value) => setDraft((c) => ({ ...c, mobile: value }))}
            />
          </FormSection>

          <FormSection title={t('contacts.portal')}>
            {/* Said in place, where the decision is made. */}
            <Alert tone="info" title={t('contacts.portalEligibilityNote')} />

            <SwitchField
              label={t('contacts.portalEnabled')}
              description={
                canManagePortal
                  ? t('contacts.portalNeedsEmail')
                  : t('contacts.portalPermissionRequired')
              }
              value={draft.portalEnabled}
              isDisabled={!canManagePortal}
              onChange={(value) =>
                setDraft((c) => ({
                  ...c,
                  portalEnabled: value,
                  portalVisibility: value
                    ? c.portalVisibility === 'none'
                      ? 'reports'
                      : c.portalVisibility
                    : 'none',
                }))
              }
            />

            <SelectField
              label={t('contacts.portalVisibilityLabel')}
              value={draft.portalVisibility}
              isDisabled={!canManagePortal || !draft.portalEnabled}
              onChange={(value) =>
                setDraft((c) => ({
                  ...c,
                  portalVisibility: value ?? 'none',
                }))
              }
              options={ALL_PORTAL_VISIBILITIES.map((visibility) => ({
                value: visibility,
                label: t(`contacts.portalVisibility.${visibility}`),
              }))}
            />

          </FormSection>

          <FormSection title={t('contacts.notes')} columns={1}>
            <TextAreaField
              label={t('contacts.notes')}
              value={draft.notes}
              onChange={(value) => setDraft((c) => ({ ...c, notes: value }))}
              rows={2}
            />
          </FormSection>
        </Form>
      </Dialog>

      <ConfirmDialog
        isOpen={deactivating !== null}
        onClose={() => setDeactivating(null)}
        title={t('contacts.deactivateTitle')}
        message={t('contacts.deactivateMessage')}
        confirmLabel={t('contacts.deactivate')}
        confirmPriority="destructive"
        isPending={setActive.isPending}
        onConfirm={() => {
          if (deactivating) {
            setActive.mutate({ contact: deactivating, isActive: false });
          }
        }}
      />
    </>
  );
}
